import React, { useState, useEffect, useRef } from 'react';
import { Shield, ExternalLink, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

/**
 * Aadhaar verification via Digio DigiLocker.
 *
 * Props:
 *  - onComplete()      — called after the user is verified (or after skip if no onSkip)
 *  - onSkip()          — optional fallback to manual upload step
 *  - userToken         — JWT (Bearer) for the authenticated customer
 *  - backendUrl        — REACT_APP_BACKEND_URL
 */
const AadhaarDigiLockerStep = ({ onComplete, onSkip, userToken, backendUrl }) => {
  const [status, setStatus] = useState('idle');
  // idle | loading | result | success | timeout | failed
  //   idle    — first render, "Verify with DigiLocker" button
  //   loading — creating the Digio request, about to open new tab
  //   result  — Digio open in new tab; polling + Check/Retry buttons
  //   success — aadhaar_verified=true, auto-advances
  //   timeout — poll ran out; user can retry
  //   failed  — network/API error or Digio reported failure
  const [error, setError] = useState('');
  const [digioUrl, setDigioUrl] = useState('');
  const pollCountRef = useRef(0);

  const apiBase = (backendUrl || '').replace(/\/+$/, '');

  // On mount, check if already verified.
  // Also detect the Digio redirect signal `?digio_status=success` in the URL —
  // when present, immediately enter result mode so we sync with Digio and
  // flip aadhaar_verified without requiring the user to click anything.
  // Second signal: localStorage.digio_started — covers cases where the browser
  // stripped the query param or the user manually navigated back.
  useEffect(() => {
    let cancelled = false;

    // 1. Read Digio redirect signal from URL (set by Digio when user returns)
    let digioRedirectSignal = null;
    let digioStartedFlag = false;
    try {
      const params = new URLSearchParams(window.location.search);
      digioRedirectSignal = params.get('digio_status'); // 'success' | 'failed' | null
      digioStartedFlag = localStorage.getItem('digio_started') === 'true';
    } catch (_) { /* non-fatal */ }

    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/kyc/aadhaar-status`, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;

        if (data && data.aadhaar_verified) {
          setStatus('success');
          clearDigioStartedFlag();
          cleanupDigioUrlParam();
          setTimeout(() => onComplete && onComplete(), 1200);
          return;
        }

        // Not yet verified, but user came back from Digio (either via our
        // redirect_url or by manually navigating back) → jump straight to the
        // result screen which auto-syncs.
        if (digioRedirectSignal === 'success' || digioStartedFlag) {
          // eslint-disable-next-line no-console
          console.log('[Aadhaar] Detected Digio return — showing result screen', {
            digioRedirectSignal, digioStartedFlag,
          });
          cleanupDigioUrlParam();
          setStatus('result');
        } else if (digioRedirectSignal === 'failed') {
          setError('DigiLocker verification was cancelled or failed. Please try again.');
          setStatus('failed');
          clearDigioStartedFlag();
          cleanupDigioUrlParam();
        }
      } catch (_) {
        /* non-fatal */
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear the localStorage flag we set before redirecting to Digio.
  const clearDigioStartedFlag = () => {
    try {
      localStorage.removeItem('digio_started');
      localStorage.removeItem('digio_request_id');
    } catch (_) { /* non-fatal */ }
  };

  // Strip `digio_status` from the URL once we've handled it, so reloads don't
  // re-trigger the flow.
  const cleanupDigioUrlParam = () => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('digio_status')) {
        url.searchParams.delete('digio_status');
        window.history.replaceState({}, '', url.toString());
      }
    } catch (_) { /* non-fatal */ }
  };

  // Re-check status whenever the tab regains focus. This catches the case
  // where the user completes DigiLocker and returns via browser back button
  // rather than our redirect_url — e.g. because the redirect was blocked or
  // the user manually navigated back.
  useEffect(() => {
    const onFocus = async () => {
      if (status === 'success' || status === 'loading') return;
      const digioStartedFlag = (() => {
        try { return localStorage.getItem('digio_started') === 'true'; } catch (_) { return false; }
      })();
      const verified = await checkAadhaarStatus();
      if (verified) {
        setStatus('success');
        clearDigioStartedFlag();
        cleanupDigioUrlParam();
        setTimeout(() => onComplete && onComplete(), 1200);
      } else if (digioStartedFlag && status !== 'result') {
        // User came back mid-flow but Digio hasn't confirmed yet — surface
        // the result screen so they can Check Again / Retry.
        setStatus('result');
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  /**
   * Combined status check:
   *   1. Calls /api/kyc/sync-digio-status — backend pings Digio API directly
   *      and flips aadhaar_verified=true if Digio reports completion. This
   *      is the fallback when the webhook is delayed or missed.
   *   2. Falls back to /api/kyc/aadhaar-status which just reads the DB.
   *
   * Returns true if verification confirmed, false otherwise.
   */
  const checkAadhaarStatus = async () => {
    try {
      // Sync first (talks to Digio API)
      const syncRes = await fetch(`${apiBase}/api/kyc/sync-digio-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
      });
      if (syncRes.ok) {
        const syncData = await syncRes.json();
        // eslint-disable-next-line no-console
        console.log('[Aadhaar Poll] Sync result:', syncData);
        if (syncData && syncData.aadhaar_verified) {
          return true;
        }
      }

      // Fallback: just read DB state (in case webhook already flipped it)
      const statusRes = await fetch(`${apiBase}/api/kyc/aadhaar-status`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        // eslint-disable-next-line no-console
        console.log('[Aadhaar Poll] DB status result:', statusData);
        if (statusData && statusData.aadhaar_verified) {
          return true;
        }
      }
      return false;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Aadhaar Poll] Error:', err);
      return false;
    }
  };

  const initiateDigiLocker = async () => {
    setStatus('loading');
    setError('');
    pollCountRef.current = 0;

    try {
      const res = await fetch(`${apiBase}/api/kyc/create-digilocker-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
      });

      const data = await res.json();

      if (res.ok && data.success && data.digio_url) {
        // Persist the fact that we're heading to Digio. Two use-cases:
        //   1. Digio honours our `redirect_url` → we detect it via query param
        //   2. Digio shows "You may close the window" (default web behaviour
        //      for most templates) → we detect it via the localStorage flag +
        //      focus/visibility listeners on this tab.
        try {
          localStorage.setItem('digio_started', 'true');
          if (data.request_id) {
            localStorage.setItem('digio_request_id', data.request_id);
          }
        } catch (_) { /* non-fatal */ }

        // Open Digio in a NEW TAB so THIS tab stays alive to poll. When we
        // used same-tab (window.location.href) our polling code was killed
        // and users got stuck when Digio didn't auto-redirect back.
        const win = window.open(data.digio_url, '_blank', 'noopener,noreferrer');
        if (!win) {
          // Popup blocked — surface the URL so user can open it manually.
          setError('Popup was blocked. Click the link below to open DigiLocker in a new tab.');
        }

        // Flip straight to the result screen — it auto-polls every 5s and
        // shows Check Again + Retry buttons.
        setStatus('result');
        setDigioUrl(data.digio_url);
      } else {
        setError(data.detail || data.message || 'Failed to initiate DigiLocker verification');
        setStatus('failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setStatus('failed');
    }
  };

  // Auto-poll for verification status while on the result screen — uses sync
  // endpoint which talks to Digio API directly. Stops on success, timeout, or
  // when the user retries (which flips status back to 'idle').
  useEffect(() => {
    let interval;

    if (status === 'result') {
      // Poll immediately first
      checkAadhaarStatus().then((verified) => {
        if (verified) {
          setStatus('success');
          clearDigioStartedFlag();
          cleanupDigioUrlParam();
          setTimeout(() => onComplete && onComplete(), 1800);
        }
      });

      interval = setInterval(async () => {
        const verified = await checkAadhaarStatus();
        if (verified) {
          setStatus('success');
          clearInterval(interval);
          clearDigioStartedFlag();
          cleanupDigioUrlParam();
          setTimeout(() => onComplete && onComplete(), 1800);
          return;
        }

        pollCountRef.current += 1;
        // ~10 minutes (120 × 5s). Digio can take several minutes to finalise
        // the KYC record after DigiLocker completion (its /response endpoint
        // 404s during that window), so we poll well past the old 2-min limit.
        if (pollCountRef.current >= 120) {
          clearInterval(interval);
          setStatus('timeout');
        }
      }, 5000);
    }

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div style={{
      width: '100%',
      maxWidth: '480px',
      margin: '0 auto',
      padding: '24px 16px',
      boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{
          width: '72px',
          height: '72px',
          borderRadius: '20px',
          backgroundColor: '#EFF6FF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <Shield size={36} color="#1A3C6E" />
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1A3C6E', margin: '0 0 8px' }}>
          Verify Aadhaar
        </h2>
        <p style={{ fontSize: '14px', color: '#64748B', margin: 0, lineHeight: '1.5' }}>
          Verify your identity securely using DigiLocker — Government of India's official platform
        </p>
      </div>

      {/* Status: Idle */}
      {status === 'idle' && (
        <div>
          <div style={{
            backgroundColor: '#F8FAFC',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
          }}>
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
              How it works:
            </p>
            {[
              'Click "Verify with DigiLocker" below',
              'Login with your Aadhaar number on DigiLocker',
              'Enter OTP sent to your Aadhaar-linked mobile',
              'Consent to share your Aadhaar data',
              'Come back here — verification completes automatically',
            ].map((stepText, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'flex-start' }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: '#1A3C6E',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '1px',
                }}>
                  {i + 1}
                </div>
                <p style={{ fontSize: '13px', color: '#4B5563', margin: 0, lineHeight: '1.4' }}>
                  {stepText}
                </p>
              </div>
            ))}
          </div>

          <div style={{
            backgroundColor: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: '24px',
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
          }}>
            <Shield size={16} color="#16A34A" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{ fontSize: '12px', color: '#15803D', margin: 0, lineHeight: '1.5' }}>
              Your Aadhaar data is verified directly by DigiLocker. Rebuilt does not store your Aadhaar number or biometric data.
            </p>
          </div>

          <button
            onClick={initiateDigiLocker}
            data-testid="digilocker-verify-btn"
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: '#1A3C6E',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '12px',
            }}
          >
            <ExternalLink size={18} />
            Verify with DigiLocker
          </button>

          {onSkip && (
            <button
              onClick={onSkip}
              data-testid="digilocker-skip-btn"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'transparent',
                color: '#64748B',
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Skip for now — Upload Aadhaar manually
            </button>
          )}
        </div>
      )}

      {/* Status: Loading */}
      {status === 'loading' && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #E2E8F0',
            borderTop: '4px solid #1A3C6E',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p style={{ color: '#64748B', fontSize: '14px' }}>Preparing DigiLocker verification…</p>
        </div>
      )}

      {/* Status: Result (Digio open in new tab — we poll here + Check/Retry) */}
      {status === 'result' && (
        <div>
          <div style={{
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            marginBottom: '20px',
          }}>
            <Clock size={32} color="#1A3C6E" style={{ marginBottom: '12px' }} />
            <p style={{ fontWeight: '600', color: '#1A3C6E', margin: '0 0 8px', fontSize: '15px' }}>
              Complete DigiLocker in the other tab
            </p>
            <p style={{ color: '#3B82F6', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
              We opened DigiLocker in a new tab. Finish the verification there, then come back to this tab —
              we'll auto-detect when it's done.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#10B981',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} />
            <p style={{ fontSize: '13px', color: '#64748B', margin: 0 }}>
              Waiting for DigiLocker to confirm…
            </p>
          </div>

          <button
            onClick={async () => {
              const verified = await checkAadhaarStatus();
              if (verified) {
                setStatus('success');
                clearDigioStartedFlag();
                cleanupDigioUrlParam();
                setTimeout(() => onComplete && onComplete(), 1500);
              } else {
                setError('Not verified yet. If you\'ve finished on DigiLocker, wait a few seconds and try again.');
              }
            }}
            data-testid="digilocker-check-status-btn"
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#1A3C6E',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '12px',
            }}
          >
            ✓ I completed DigiLocker — Check Again
          </button>

          {digioUrl && (
            <button
              onClick={() => window.open(digioUrl, '_blank', 'noopener,noreferrer')}
              data-testid="digilocker-reopen-btn"
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'transparent',
                color: '#1A3C6E',
                border: '1px solid #1A3C6E',
                borderRadius: '12px',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                marginBottom: '12px',
              }}
            >
              <ExternalLink size={14} />
              Reopen DigiLocker Tab
            </button>
          )}

          <button
            onClick={() => {
              clearDigioStartedFlag();
              setDigioUrl('');
              setError('');
              setStatus('idle');
            }}
            data-testid="digilocker-retry-btn"
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'transparent',
              color: '#B45309',
              border: '1px dashed #B45309',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '12px',
            }}
          >
            🔄 Start Over
          </button>

          <div style={{
            backgroundColor: '#F0F9FF',
            borderRadius: '10px',
            padding: '14px',
            marginTop: '4px',
          }}>
            <p style={{ fontSize: '12px', color: '#0369A1', margin: 0, lineHeight: '1.6' }}>
              <strong>Having trouble?</strong><br />
              • Make sure you completed all steps on DigiLocker<br />
              • Your Aadhaar must be linked to your mobile number<br />
              • Enter the OTP sent to your Aadhaar-linked mobile<br />
              • Click Allow/Consent when DigiLocker asks<br />
              • If DigiLocker says "You may close the window" — close that tab and come back here
            </p>
          </div>

          {error && (
            <p style={{ marginTop: '12px', fontSize: '12px', color: '#B91C1C', textAlign: 'center' }}>{error}</p>
          )}
        </div>
      )}

      {/* Status: Success */}
      {status === 'success' && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            backgroundColor: '#F0FDF4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <CheckCircle size={40} color="#16A34A" />
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#15803D', margin: '0 0 8px' }}>
            Aadhaar Verified!
          </h3>
          <p style={{ fontSize: '14px', color: '#64748B', margin: 0 }}>
            Your Aadhaar has been successfully verified via DigiLocker.
          </p>
        </div>
      )}

      {/* Status: Timeout */}
      {status === 'timeout' && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Clock size={40} color="#F59E0B" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#92400E', margin: '0 0 8px' }}>
            Taking longer than expected
          </h3>
          <p style={{ fontSize: '14px', color: '#64748B', margin: '0 0 20px' }}>
            Please complete the verification in DigiLocker and come back.
          </p>
          <button
            onClick={async () => {
              pollCountRef.current = 0;
              const verified = await checkAadhaarStatus();
              if (verified) {
                setStatus('success');
                clearDigioStartedFlag();
                setTimeout(() => onComplete && onComplete(), 1500);
              } else {
                setStatus('result');
              }
            }}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#1A3C6E',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              cursor: 'pointer',
              marginBottom: '12px',
            }}
          >
            Check Status Again
          </button>
          <button
            onClick={initiateDigiLocker}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'transparent',
              color: '#64748B',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Start Over
          </button>
        </div>
      )}

      {/* Status: Failed */}
      {status === 'failed' && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <AlertTriangle size={40} color="#EF4444" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#991B1B', margin: '0 0 8px' }}>
            Verification Failed
          </h3>
          <p style={{ fontSize: '14px', color: '#64748B', margin: '0 0 20px' }}>
            {error || 'Something went wrong. Please try again.'}
          </p>
          <button
            onClick={() => { setStatus('idle'); setError(''); }}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#1A3C6E',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              cursor: 'pointer',
              marginBottom: '12px',
            }}
          >
            Try Again
          </button>
          {onSkip && (
            <button
              onClick={onSkip}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: 'transparent',
                color: '#64748B',
                border: '1px solid #E2E8F0',
                borderRadius: '12px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Upload Aadhaar manually instead
            </button>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default AadhaarDigiLockerStep;
